import type { ApiSchema } from "../apiSchema";
import { enumMembers } from "../apiSchema";
import { jsonBody, requestJson, withQuery } from "../http/httpClient";
import { readArray, readBoolean, readInteger, readNullableString, readNullableInteger, readObject, readOneOf, readString, readStringRecord } from "../http/jsonDecoding";
import { readChangeSetSummary, type ChangeSetState, type ChangeSetSummary } from "./reviewDecoders";
import { corpusReviewRequest } from "./corpusReviewRequest";

/**
 * Corpus imports a Workflow proposed (content.md Q-30, processes.md P-7): a change set of a
 * corpus whose documents a tool made from a file on the host's PC. A curator of the corpus
 * merges it, which is the new corpus version; until then nothing is imported.
 */

export type DocumentChange = ApiSchema<"DocumentChangeKind">;
export type ImportMode = ApiSchema<"ImportMode">;

const DOCUMENT_CHANGES = enumMembers<DocumentChange>({ added: true, modified: true, removed: true });
const IMPORT_MODES = enumMembers<ImportMode>({ upsert: true, replace_all: true });

export interface ImportedSourceFile {
  readonly input: string;
  /** Relative to the host's workspace; the file stays on that PC. */
  readonly path: string;
  readonly sha256: string;
  readonly sizeBytes: number;
}

export interface ImportProvenance {
  /** The tool's `VersionRef`. */
  readonly tool: string;
  readonly runtime: string;
  readonly environmentDigest: string | null;
  readonly sourceFiles: readonly ImportedSourceFile[];
  readonly importSha256: string;
  readonly mode: ImportMode;
}

export interface ImportedDocument {
  readonly change: DocumentChange;
  readonly itemId: string;
  readonly key: string;
  readonly characters: number;
  readonly segments: number;
  readonly metadata: Readonly<Record<string, string>>;
  readonly oldReference: string | null;
  readonly newReference: string | null;
}

export interface ImportProposal {
  readonly changeSet: ChangeSetSummary;
  readonly corpusId: string;
  readonly corpusName: string;
  /** The head a merge names: exactly what the curator saw. */
  readonly head: string;
  /** The corpus has a newer version than the import was planned on; it cannot merge. */
  readonly corpusMoved: boolean;
  readonly segmentationChanged: boolean;
  readonly provenance: ImportProvenance | null;
  readonly documents: readonly ImportedDocument[];
  readonly counts: ChangeCounts;
  readonly offset: number;
  readonly limit: number;
  readonly nextOffset: number | null;
  /** The user curates this corpus, so may merge or scrap. */
  readonly mayReview: boolean;
}

function readProvenance(value: unknown): ImportProvenance {
  const body = readObject(value, "import provenance");
  return {
    tool: readString(body, "tool"),
    runtime: readString(body, "runtime"),
    environmentDigest: readNullableString(body, "environment_digest"),
    sourceFiles: readArray(body["source_files"], "source_files", (item) => {
      const file = readObject(item, "source file");
      return { input: readString(file, "input"), path: readString(file, "path"), sha256: readString(file, "sha256"), sizeBytes: readInteger(file, "size_bytes") };
    }),
    importSha256: readString(body, "import_sha256"),
    mode: readOneOf(body, "mode", IMPORT_MODES),
  };
}

export function readImportProposal(value: unknown): ImportProposal {
  const body = readObject(value, "corpus import");
  const provenance = body["provenance"];
  return {
    changeSet: readChangeSetSummary(body["change_set"]),
    corpusId: readString(body, "corpus_id"),
    corpusName: readString(body, "corpus_name"),
    head: readString(body, "head"),
    corpusMoved: readBoolean(body, "corpus_moved"),
    segmentationChanged: readBoolean(body, "segmentation_changed"),
    provenance: provenance === null || provenance === undefined ? null : readProvenance(provenance),
    documents: readArray(body["documents"], "documents", (item) => {
      const document = readObject(item, "document");
      return {
        change: readOneOf(document, "change", DOCUMENT_CHANGES),
        itemId: readString(document, "item_id"),
        key: readString(document, "key"),
        characters: readInteger(document, "characters"),
        segments: readInteger(document, "segments"),
        metadata: readStringRecord(document, "metadata"),
        oldReference: readNullableString(document, "old_reference"),
        newReference: readNullableString(document, "new_reference"),
      };
    }),
    counts: readChangeCounts(body["counts"]),
    offset: readInteger(body, "offset"),
    limit: readInteger(body, "limit"),
    nextOffset: readNullableInteger(body, "next_offset"),
    mayReview: readBoolean(body, "may_review"),
  };
}

const IMPORT_PAGE_SIZE = 50;

/** The project's proposed corpus imports, newest first. */
export function listImportProposals(projectId: string, state: ChangeSetState | null, signal?: AbortSignal): Promise<readonly ChangeSetSummary[]> {
  return requestJson({
    method: "GET",
    path: withQuery(`/projects/${encodeURIComponent(projectId)}/corpus-change-sets`, { state, limit: IMPORT_PAGE_SIZE }),
    signal,
    decodeResponse: (body) => readArray(body, "corpus change sets", readChangeSetSummary),
  });
}

export function getImportProposal(changeSetId: string, signal?: AbortSignal, offset = 0, reviewedHead: string | null = null): Promise<ImportProposal> {
  return requestJson({ method: "GET", path: withQuery(`/content/corpus-change-sets/${encodeURIComponent(changeSetId)}`, { offset, limit: IMPORT_PAGE_SIZE, reviewed_head: reviewedHead }), signal, decodeResponse: readImportProposal });
}

/** Merge exactly the head the curator saw: the new corpus version. */
export function mergeImportProposal(changeSetId: string, reviewedHead: string, reason: string, acknowledgement: string | null = null): Promise<ImportProposal> {
  return corpusReviewRequest({
    method: "POST",
    path: `/content/corpus-change-sets/${encodeURIComponent(changeSetId)}/merge`,
    body: jsonBody({ reviewed_head: reviewedHead, reason, impact_acknowledgement: acknowledgement === null ? null : { note: acknowledgement } } satisfies ApiSchema<"CorpusMergeBody">),
    decodeResponse: readImportProposal,
  });
}

export function scrapImportProposal(changeSetId: string, reason: string): Promise<ImportProposal> {
  return requestJson({
    method: "POST",
    path: `/content/corpus-change-sets/${encodeURIComponent(changeSetId)}/scrap`,
    body: jsonBody({ reason } satisfies ApiSchema<"TransitionBody">),
    decodeResponse: readImportProposal,
  });
}


export interface ChangeCounts {
  readonly added: number;
  readonly modified: number;
  readonly removed: number;
  readonly unchanged: number;
  readonly total: number;
}

export function readChangeCounts(value: unknown): ChangeCounts {
  const body = readObject(value, "change counts");
  return { added: readInteger(body, "added"), modified: readInteger(body, "modified"), removed: readInteger(body, "removed"), unchanged: readInteger(body, "unchanged"), total: readInteger(body, "total") };
}
